import { isPossiblePhoneNumber, parsePhoneNumber, PhoneNumber } from 'libphonenumber-js';
import { Country } from 'react-phone-number-input';

/**
 * Parse a national or free-form phone for **read/display** call sites.
 *
 * Gates on `isPossiblePhoneNumber` (length) only — not `isValid()` — so stored
 * numbers that are length-possible but fail numbering-plan validation in the
 * bundled libphonenumber metadata still format for display (ConfirmedPage links,
 * etc.). Trunk prefixes (e.g. French/UK leading `0`) are still stripped via
 * libphonenumber parse.
 *
 * For write/input paths that must fail closed on possible-but-invalid national
 * input (HOS-2391), use {@link getValidPhone}.
 *
 * @param number - National digits or free-form phone as typed/stored
 * @param defaultCountry - ISO country used when the input is not already E.164
 * @returns Parsed phone when length-possible, otherwise `null`
 * @example getPhone('0760357094', 'FR')?.number // '+33760357094'
 * @example getPhone('07', 'FR') // null
 */
export const getPhone = (
  number: string | undefined,
  defaultCountry: Country | undefined = 'US'
): PhoneNumber | null => {
  if (!number) {
    return null;
  }

  if (number === '') {
    return null;
  }

  const country = defaultCountry ?? 'US';

  if (!isPossiblePhoneNumber(number, country)) {
    return null;
  }

  return parsePhoneNumber(number, country);
};

/**
 * Parse a national or free-form phone for **write/input** call sites.
 *
 * Same trunk-safe parse as {@link getPhone}, but also requires `isValid()` so
 * incomplete/possible-but-invalid national input (e.g. FR `061234567`) cannot
 * publish a `+330…` twin into state (HOS-2391). Use from DetailsPage, rta-submit,
 * and any other path that persists a phone.
 *
 * @param number - National digits or free-form phone as typed
 * @param defaultCountry - ISO country used when the input is not already E.164
 * @returns Parsed phone when valid, otherwise `null`
 * @example getValidPhone('0760357094', 'FR')?.number // '+33760357094'
 * @example getValidPhone('061234567', 'FR') // null
 */
export const getValidPhone = (
  number: string | undefined,
  defaultCountry: Country | undefined = 'US'
): PhoneNumber | null => {
  const parsed = getPhone(number, defaultCountry);
  if (!parsed?.isValid()) {
    return null;
  }
  return parsed;
};
